import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isTokenExpired
} from "../utils/tokenUtils";

// Prefer environment variable, fallback to default URL
// Supported env vars:
// - REACT_APP_API_URL (legacy in this repo)
// - REACT_APP_API_BASE_URL (documented in SAML integration guide)
const API_URL =
  process.env.REACT_APP_API_URL;

// Primary SAML login URL:
// - If your org provides an IdP-initiated Google "initsso" link, set:
//   REACT_APP_SAML_IDP_INITSSO_URL=https://accounts.google.com/o/saml2/initsso?idpid=...&spid=...&forceauthn=false
// - Otherwise, we fall back to SP-initiated backend endpoint.
export const getSamlLoginUrl = () =>
 "https://accounts.google.com/o/saml2/initsso?idpid=C011hh8dl&spid=324181964422&forceauthn=false" || `${API_URL}/auth/saml/login`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ============================================
// REFRESH TOKEN STATE MANAGEMENT
// ============================================

// Flag to track if a refresh is in progress
let isRefreshing = false;

// Queue of requests waiting for token refresh
let refreshSubscribers = [];

/**
 * Subscribe a request to be retried after token refresh
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all waiting requests with the new token
 */
const onTokenRefreshed = (newAccessToken) => {
  refreshSubscribers.forEach(callback => callback(newAccessToken));
  refreshSubscribers = [];
};

/**
 * Notify all waiting requests of refresh failure
 */
const onRefreshFailed = (error) => {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
};

/**
 * Perform the token refresh
 */
const performTokenRefresh = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Call refresh endpoint directly (avoid interceptor)
  const response = await axios.post(`${API_URL}/auth/refresh`, {
    refreshToken
  });

  const { accessToken, token, refreshToken: newRefreshToken } = response.data;

  // Support both 'accessToken' and 'token' naming from backend
  const newAccessToken = accessToken || token;

  // Store new tokens (backend may rotate refresh token)
  setTokens(newAccessToken, newRefreshToken || refreshToken);

  return newAccessToken;
};

// ============================================
// REQUEST INTERCEPTOR
// ============================================

api.interceptors.request.use(
  async (config) => {
    // Skip auth for refresh requests to avoid infinite loops
    if (config._skipAuthRefresh) {
      return config;
    }

    const token = getAccessToken();

    if (token) {
      // PROACTIVE REFRESH: Check if token is about to expire
      // Refresh 60 seconds before expiry to ensure smooth UX
      if (isTokenExpired(60) && !isRefreshing && getRefreshToken()) {
        try {
          isRefreshing = true;
          const newToken = await performTokenRefresh();
          config.headers.Authorization = `Bearer ${newToken}`;
          isRefreshing = false;
          onTokenRefreshed(newToken);
        } catch (error) {
          isRefreshing = false;
          // Don't clear tokens here - let the response interceptor handle 401
          // Just use the existing (possibly expired) token
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else if (isRefreshing) {
        // If refresh is in progress, wait for it
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken, error) => {
            if (error) {
              reject(error);
            } else if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(config);
            } else {
              config.headers.Authorization = `Bearer ${token}`;
              resolve(config);
            }
          });
        });
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip retry for refresh requests or already retried requests
    if (originalRequest._skipAuthRefresh || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Check for specific refresh token expired error code
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || error.response?.data?.error;

      if (errorCode === 'REFRESH_TOKEN_EXPIRED' ||
          errorCode === 'INVALID_REFRESH_TOKEN' ||
          errorMessage?.toLowerCase().includes('refresh token')) {
        // Refresh token is expired - full logout required
        clearTokens();
        window.location.assign("/login");
        return Promise.reject(error);
      }

      // Check if we have a refresh token to attempt refresh
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // No refresh token - clear and redirect
        clearTokens();
        window.location.assign("/login");
        return Promise.reject(error);
      }

      // Attempt to refresh the token
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newAccessToken = await performTokenRefresh();
          isRefreshing = false;
          onTokenRefreshed(newAccessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailed(refreshError);

          // Clear tokens and redirect to login
          clearTokens();
          window.location.assign("/login");
          return Promise.reject(refreshError);
        }
      } else {
        // Another request is already refreshing - wait for it
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken, refreshError) => {
            if (refreshError) {
              reject(refreshError);
            } else if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH SERVICE (Keep existing for backward compat)
// ============================================

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      return response;
    } catch (error) {
      throw error;
    }
  },

  profile: async () => {
    try {
      const response = await api.get("/auth/profile");
      return response;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
